import React, { useEffect, useRef, useState } from "react"
import { Alert, Button, Col, Nav, NavItem, NavLink, Offcanvas, OffcanvasBody, OffcanvasHeader, Row, TabContent, TabPane } from "reactstrap"
import SfLoadingSkeleton from "components/Common/Loadings/SfLoadingSkeleton"
import MiloTimeline from "./MiloTimeline"
import MiloCharts from "./MiloCharts"
import { MILO_LOAD_ERROR, fetchMiloCampaignHistory } from "./miloApi"
import { fetchMiloCampaignMetrics } from "./miloMockApi"
import {
    CapUsedBar,
    DecisionBadge,
    PaceBadge,
    SuggestionCell,
    formatCapValue,
    formatLocalTime,
    formatMoney,
    getReasonText,
} from "./milo_helper"

const TABS = { TIMELINE: "timeline", CHARTS: "charts" }

const SummaryTile = ({ label, children }) => (
    <Col sm={6} className="mb-3">
        <div className="border rounded p-2 h-100">
            <div className="text-muted font-size-12 mb-1">{label}</div>
            {children}
        </div>
    </Col>
)

const MiloCampaignDrawer = ({ campaign, onClose }) => {
    const [activeTab, setActiveTab] = useState(TABS.TIMELINE)
    const [cycles, setCycles] = useState([])
    const [historyMeta, setHistoryMeta] = useState({})
    const [metrics, setMetrics] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [loadingOlder, setLoadingOlder] = useState(false)
    const [olderError, setOlderError] = useState(null)
    const campaignId = campaign?.campaign_id
    // Drops an "older checks" page that arrives after the panel moved to another campaign.
    const openCampaignId = useRef(campaignId)
    openCampaignId.current = campaignId

    useEffect(() => {
        if (!campaignId) return undefined
        let cancelled = false
        setActiveTab(TABS.TIMELINE)
        setCycles([])
        setHistoryMeta({})
        setMetrics(null)
        setLoading(true)
        setError(null)
        setLoadingOlder(false)
        setOlderError(null)
        fetchMiloCampaignHistory(campaignId)
            .then(body => {
                if (cancelled) return
                setCycles(body.data?.cycles || [])
                setHistoryMeta(body.additional_data || {})
            })
            .catch(e => !cancelled && setError(e.message || MILO_LOAD_ERROR))
            .finally(() => !cancelled && setLoading(false))
        // Charts still use sample data, so they load on their own and never hide the timeline.
        fetchMiloCampaignMetrics(campaignId)
            .then(body => !cancelled && setMetrics(body.data))
            .catch(() => {})
        return () => { cancelled = true }
    }, [campaignId])

    // History is paged newest first, 50 checks at a time. New checks keep arriving, so the next
    // page can repeat a check already shown; repeats are dropped by cycle_id.
    const loadOlderChecks = () => {
        const requestedFor = campaignId
        setLoadingOlder(true)
        setOlderError(null)
        fetchMiloCampaignHistory(campaignId, (historyMeta.page_number || 1) + 1)
            .then(body => {
                if (openCampaignId.current !== requestedFor) return
                setCycles(prev => {
                    const seen = new Set(prev.map(c => c.cycle_id))
                    return [...prev, ...(body.data?.cycles || []).filter(c => !seen.has(c.cycle_id))]
                })
                setHistoryMeta(body.additional_data || {})
            })
            .catch(e => openCampaignId.current === requestedFor && setOlderError(e.message || MILO_LOAD_ERROR))
            .finally(() => openCampaignId.current === requestedFor && setLoadingOlder(false))
    }

    const decision = campaign?.latest_decision
    const pacing = campaign?.pacing

    return (
        <Offcanvas isOpen={!!campaign} toggle={onClose} direction="end" style={{ width: 760, maxWidth: "100vw" }}>
            {campaign && (
                <>
                    <OffcanvasHeader toggle={onClose} className="border-bottom">
                        <div>{campaign.campaign_name}</div>
                        <div className="text-muted font-size-12 fw-normal">
                            CID {campaign.campaign_id} · AID {campaign.account_id} · {campaign.account_name} · <span className="text-capitalize">{campaign.lead_type}</span>
                        </div>
                    </OffcanvasHeader>
                    <OffcanvasBody>
                        <Alert color="info" className="py-2 font-size-12">
                            <i className="bx bx-info-circle me-1" />
                            Shadow mode: MILO&apos;s suggestions are recorded but <b>not applied</b> to live prices.
                        </Alert>

                        <Row>
                            <SummaryTile label="Latest MILO decision">
                                <div className="d-flex align-items-center gap-2 mb-1">
                                    <DecisionBadge type={campaign.decision_type} dial={decision?.dial_percent} />
                                    <SuggestionCell decision={decision} />
                                </div>
                                <div className="font-size-13">{getReasonText(decision?.reason_code) || "No suggestion for this check."}</div>
                                <div className="text-muted font-size-12 mt-1">
                                    Checked {formatLocalTime(decision?.decided_at, pacing?.timezone, true)}
                                </div>
                            </SummaryTile>
                            <SummaryTile label="Price (24h median)">
                                <div className="d-flex align-items-baseline gap-2">
                                    <span className="font-size-18 fw-semibold">{formatMoney(campaign.prices?.current_price)}</span>
                                    <span className="text-muted">→</span>
                                    <span className="font-size-16 fst-italic text-muted">{formatMoney(campaign.prices?.suggested_price)}</span>
                                    <span className="badge badge-soft-dark">Estimate</span>
                                </div>
                                <div className="text-muted font-size-12 mt-1">
                                    Floor {formatMoney(campaign.prices?.floor_price)} · Applied: — (not in Phase 1) · {campaign.prices?.offers_24h ?? 0} offers in 24h
                                </div>
                            </SummaryTile>
                            <SummaryTile label={`Today's cap · ${pacing?.local_date || ""}`}>
                                <div className="font-size-13 mb-1">{formatCapValue(pacing)}</div>
                                <CapUsedBar pacing={pacing} />
                            </SummaryTile>
                            <SummaryTile label="Pace">
                                <PaceBadge pacing={pacing} />
                                <div className="text-muted font-size-12 mt-1">
                                    Below 0.90 counts as behind. Measured on the account&apos;s local day ({pacing?.timezone}), not PST.
                                </div>
                            </SummaryTile>
                        </Row>

                        <Nav tabs className="nav-tabs-custom mb-3">
                            <NavItem>
                                <NavLink className={activeTab === TABS.TIMELINE ? "active" : ""} onClick={() => setActiveTab(TABS.TIMELINE)} style={{ cursor: "pointer" }}>
                                    Decision history (7 days)
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink className={activeTab === TABS.CHARTS ? "active" : ""} onClick={() => setActiveTab(TABS.CHARTS)} style={{ cursor: "pointer" }}>
                                    Charts
                                </NavLink>
                            </NavItem>
                        </Nav>

                        {error && <Alert color="danger">{error}</Alert>}
                        {loading && <SfLoadingSkeleton rows={6} cols={1} />}
                        {!loading && !error && (
                            <TabContent activeTab={activeTab}>
                                <TabPane tabId={TABS.TIMELINE}>
                                    <MiloTimeline cycles={cycles} />
                                    {historyMeta.has_next_page && (
                                        <div className="text-center mt-3">
                                            {olderError && <div className="text-danger font-size-12 mb-2">{olderError}</div>}
                                            <Button color="light" size="sm" onClick={loadOlderChecks} disabled={loadingOlder}>
                                                {loadingOlder ? "Loading..." : "Show older checks"}
                                            </Button>
                                        </div>
                                    )}
                                </TabPane>
                                <TabPane tabId={TABS.CHARTS}>
                                    {/* Mount charts only when visible: ApexCharts sizes itself wrong inside a hidden tab. */}
                                    {activeTab === TABS.CHARTS && <MiloCharts metrics={metrics} />}
                                </TabPane>
                            </TabContent>
                        )}
                    </OffcanvasBody>
                </>
            )}
        </Offcanvas>
    )
}

export default MiloCampaignDrawer
